/**
 * Fehlerseite fuer alle Seiten der App - statt der englischen
 * Entwicklerseite von React Router. Sie erscheint IN der App-Huelle:
 * Seitenleiste und Bereichsfilter bleiben, man kann einfach weiterklicken.
 */
import { useEffect, useState } from 'react';
import { Link, useRouteError } from 'react-router-dom';

import { istNachladeFehler, neustartErlaubt, serverErreichbar } from '@/app/nachladeFehler';

type Zustand = 'pruefe' | 'server-weg' | 'fehler';

export function RouteFehler() {
  const error = useRouteError();
  const nachladen = istNachladeFehler(error);
  const [zustand, setZustand] = useState<Zustand>(nachladen ? 'pruefe' : 'fehler');

  useEffect(() => {
    if (!nachladen) return;
    let aktiv = true;
    void serverErreichbar().then((erreichbar) => {
      if (!aktiv) return;
      // Server da: die App wurde aktualisiert - einmal neu laden genuegt.
      if (erreichbar && neustartErlaubt()) {
        window.location.reload();
        return;
      }
      setZustand(erreichbar ? 'fehler' : 'server-weg');
    });
    return () => { aktiv = false; };
  }, [nachladen]);

  if (zustand === 'pruefe') {
    return <p className="px-6 py-10 text-sm text-ink-faint">Seite wird neu geladen …</p>;
  }

  const text = error instanceof Error ? error.message : String(error);

  return (
    <div role="alert" className="mx-auto flex max-w-xl flex-col gap-4 px-6 py-12">
      <h1 className="text-lg font-semibold">Diese Seite konnte nicht geladen werden</h1>

      {zustand === 'server-weg' ? (
        <div className="flex flex-col gap-2 text-sm leading-relaxed text-ink-muted">
          <p>
            Die App erreicht ihren Server nicht. Wahrscheinlich läuft er nicht mehr –
            zum Beispiel, weil das Terminal geschlossen wurde oder der Laptop im
            Ruhezustand war.
          </p>
          <p>
            So geht es weiter: Im Linux-Terminal <code className="rounded bg-surface-raised px-1.5 py-0.5 text-ink">cd ~/Ole-Privat && npm run dev</code> eingeben,
            das Terminal offen lassen und dann hier auf „Neu laden“ drücken. Zeigt das
            Terminal eine andere Adresse (z. B. Port 5174 statt 5173), diese öffnen.
          </p>
        </div>
      ) : (
        <p className="text-sm leading-relaxed text-ink-muted">
          Lade die Seite neu. Hilft das nicht, lade in den Einstellungen eine Sicherung
          herunter, bevor du weitermachst.
        </p>
      )}

      <p className="text-sm text-ink-muted">Deine Daten sind nicht betroffen – sie liegen unverändert in diesem Browser.</p>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="h-9 rounded-md bg-accent px-3.5 text-sm font-medium text-on-accent"
        >
          Neu laden
        </button>
        <Link to="/" className="grid h-9 place-items-center rounded-md border border-line px-3.5 text-sm text-ink-muted hover:text-ink">
          Zur Übersicht
        </Link>
      </div>

      <details className="text-[12px] text-ink-faint">
        <summary className="cursor-pointer">Technische Details</summary>
        <pre className="mt-2 overflow-x-auto rounded-md border border-line bg-surface-raised p-3 text-ink-muted">{text}</pre>
      </details>
    </div>
  );
}
